import axios from 'axios';

import { UsersApiFactory } from '@/api/generated/apis/users-api';
import { type UserBasicDto } from '@/api/generated/models/user-basic-dto';
import { getApiConfiguration } from '@/api/util/useApiClient.util.ts';
import CONFIG from '@/config/config.ts';
import { type UserModel } from '@/contexts/userContext/User.context.ts';

type CurrentUser = UserBasicDto | UserModel;

let currentUserSnapshot: CurrentUser | undefined;
let currentUserPromise: Promise<UserBasicDto> | undefined;

// Bare instance on purpose: reading the current user is a cookie-auth GET,
// so the mutation-only CSRF interceptor is irrelevant here.
const usersApi = UsersApiFactory(getApiConfiguration(CONFIG.HOST), undefined, axios.create());

const getUserId = (user: CurrentUser): string | undefined =>
  'userId' in user ? user.userId : user.id;

/**
 * Mirrors the user identity the React layer holds in UserContext so route
 * loaders (which run outside React) can authorize without a fetch (OCD-309).
 * Values without an identity are ignored: several call sites spread
 * UserDefault into setUser and must not poison the snapshot.
 */
export const syncCurrentUserSnapshot = (user: CurrentUser): void => {
  if (!getUserId(user)) {
    return;
  }
  currentUserSnapshot = user;
};

/**
 * Single in-flight fetch of the current user shared by route loaders and the
 * UserContextProvider query, so a cold start costs exactly one request. A
 * failed attempt is forgotten so the next route entry can retry.
 */
export const ensureCurrentUser = (): Promise<UserBasicDto> => {
  const fetchCurrentUser = (): Promise<UserBasicDto> =>
    usersApi
      .getCurrentUser()
      .then(response => {
        const user = response?.data;
        if (user) {
          currentUserSnapshot = user;
        }
        return user;
      })
      .catch((error: unknown) => {
        currentUserPromise = undefined;
        throw error;
      });
  currentUserPromise ??= fetchCurrentUser();
  return currentUserPromise;
};

/**
 * Loader-facing read: the live snapshot when the React layer already has the
 * user, otherwise the shared fetch (first direct entry into a guarded URL).
 */
export const readCurrentUser = (): Promise<CurrentUser> =>
  currentUserSnapshot ? Promise.resolve(currentUserSnapshot) : ensureCurrentUser();

/** Test seam: clears the snapshot and the memoized fetch between tests. */
export const resetCurrentUserSnapshot = (): void => {
  currentUserSnapshot = undefined;
  currentUserPromise = undefined;
};
